import { Alert, Checkbox, HTMLSelect } from '@blueprintjs/core';
import { ReactNode, createContext, useState } from 'react';
import styles from './ConfirmDialogProvider.module.css';
import { Product } from '@axo/shared/data-access/types';

interface IConfirmDialog {
  children?: ReactNode;
}

export interface IDialog {
  message: string;
  confirmText: string;
  cancelText: string;
  confirmCallback?: (data?: DialogFormState) => void;
  formMode?: Array<DialogStateFormMode>;
  formProps?: Record<string, unknown>;
}

interface IDialogState extends IDialog {
  isOpen: boolean;
}

const initDialogState = {
  isOpen: false,
  message: '',
  confirmText: '',
  cancelText: 'Cancel',
};

interface IDialogContext {
  confirm?: (options: IDialog) => void;
}

export enum DialogStateFormMode {
  CO_APPLICANT = 'coApplicant',
  PRODUCT_TYPE = 'productType',
}

export interface DialogFormState {
  coApplicant: CloneApplicantFormState;
  product: ProductTypeFormState;
}

interface ProductTypeFormState {
  selected: Product | unknown;
  current: Product | unknown;
}

interface CloneApplicantFormState {
  cloneCoApplicant: boolean;
  cloneCoApplicantLabel: string;
}

const formInitState = {
  coApplicant: {
    cloneCoApplicant: true,
    cloneCoApplicantLabel: 'Clone co-applicant data in the process?',
  },
  product: {
    selected: Product.SecuredLoan,
    current: Product.SecuredLoan,
  },
};

export const DialogContext = createContext<IDialogContext>({});

export const ConfirmDialogProvider = ({ children }: IConfirmDialog) => {
  const [dialogState, setDialogState] = useState<IDialogState>(initDialogState);
  const [formState, setFormState] = useState<DialogFormState>(formInitState);

  const close = () => {
    setDialogState(initDialogState);
  };

  const confirm = (options: IDialog) => {
    setDialogState({
      message: options.message,
      confirmText: options.confirmText,
      confirmCallback: options.confirmCallback,
      isOpen: true,
      cancelText: options.cancelText,
      formMode: options.formMode,
    });

    setFormState((prevState) => {
      return {
        ...prevState,
        product: {
          current: options.formProps?.product,
          selected: options.formProps?.product,
        },
      };
    });
  };

  const handleSelectedProductType = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFormState((prevState) => {
      return {
        ...prevState,
        product: {
          ...prevState.product,
          selected: e.target.value,
        },
      };
    });
  };

  const handleConfirm = () => {
    if (dialogState.confirmCallback) {
      dialogState.confirmCallback(formState);
    }
  };
  return (
    <DialogContext.Provider value={{ confirm }}>
      {children}
      <Alert
        isOpen={dialogState.isOpen}
        canOutsideClickCancel={true}
        cancelButtonText={dialogState.cancelText}
        onCancel={close}
        onConfirm={() => {
          handleConfirm();
          close();
        }}
        confirmButtonText={dialogState.confirmText}
        icon="warning-sign"
        intent="danger"
      >
        <p>{dialogState.message}</p>
        {dialogState.formMode?.includes(DialogStateFormMode.PRODUCT_TYPE) ? (
          <div className={`${styles.formItem} ${styles.products}`}>
            <span className={styles.formItemLabel}>
              Current Product type:{' '}
              <strong>{formState.product.current as string}</strong>
            </span>
            <span className={styles.formItemLabel}>Update Product type:</span>
            <HTMLSelect
              value={formState.product.selected as string}
              onChange={handleSelectedProductType}
            >
              {Object.keys(Product)?.map((product) => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </HTMLSelect>
          </div>
        ) : null}
        {dialogState.formMode?.includes(DialogStateFormMode.CO_APPLICANT) ? (
          <Checkbox
            className={styles.formItem}
            checked={formState.coApplicant?.cloneCoApplicant}
            onClick={() =>
              setFormState((prevState) => {
                return {
                  ...prevState,
                  coApplicant: {
                    ...prevState.coApplicant,
                    cloneCoApplicant: !formState.coApplicant?.cloneCoApplicant,
                  },
                };
              })
            }
          >
            {formState.coApplicant?.cloneCoApplicantLabel}
          </Checkbox>
        ) : null}
      </Alert>
    </DialogContext.Provider>
  );
};
